import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnDestroy,
  Type,
  ViewContainerRef,
  AfterViewInit,
  ViewChildren,
  QueryList,
  ChangeDetectorRef,
} from '@angular/core';
import { DateTime } from 'luxon';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { CalendarDay, CalendarWeek } from '@app/shared/calendar/calendar.model';
import { ForecastCalendarItem } from '@app/admin/statistics/forecast/model/forecast.model';
import { IdName } from '@api/models/idname';
@Component({
  selector: 'flow-calendar-item',
  templateUrl: './calendar-item.component.html',
  styleUrls: ['./calendar-item.component.scss'],
})
export class CalendarItemComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroy$ = new Subject<void>();
  @Input() day!: CalendarDay<ForecastCalendarItem | IdName>;
  @Input() item!: CalendarWeek<ForecastCalendarItem | IdName>;
  @Input() years!: number[];
  @Input() colHeight: number = 142;
  @Input() selected: boolean;

  @Input() componentType!: Type<any>;
  @Input() componentData?: any;
  @ViewChildren('container', { read: ViewContainerRef })
  containers!: QueryList<ViewContainerRef>;

  @Output() selectedBooking = new EventEmitter<{ nativeElement: HTMLElement; options: CalendarDay<any> }>();
  @ViewChild('bookingRef', { static: false }) bookingRef!: ElementRef;

  today!: boolean;
  pastDay!: boolean;
  weekRange: string[] = [];

  constructor(private translateService: TranslateService, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.years) {
        this.calcWeekRange();
      }
    });

    if (this.day) {
      const { today, pastDay } = this.setTodayAndPastDates(this.day.date);
      this.pastDay = pastDay;
      this.today = today;
    }

    if (typeof this.item === 'object' && !Array.isArray(this.item) && this.item !== null) {
      this.calcWeekRange();
    }
  }

  ngAfterViewInit() {
    this.loadComponent();
  }

  private loadComponent() {
    if (!this.containers || this.containers.length === 0) {
      return;
    }

    this.containers.forEach(container => {
      container.clear();

      // Pass data to the dynamically created component
      if (this.componentData) {
        this.componentData.forEach((item: any) => {
          const componentRef = container.createComponent(this.componentType);
          componentRef.instance.item = item;
        });
      }
    });

    this.cdr.detectChanges();
  }

  calcWeekRange() {
    const range = this.getWeekRange(this.item);
    this.weekRange = range[0] === range[1] ? [range[0]] : range;
  }

  setTodayAndPastDates(date: string) {
    const inputDate = DateTime.fromISO(date);
    const today = DateTime.local().toLocal();

    return {
      today: inputDate.hasSame(today, 'day'),
      pastDay: inputDate.toISODate() < today.toISODate(),
    };
  }

  formatMonth(monthNumber: number): string {
    const month = DateTime.fromObject({ month: monthNumber, day: 1 }).setLocale('en').toLocaleString({ month: 'long' }).toLowerCase();
    return monthNumber ? this.translateService.instant(`global.months.${month}`) : null;
  }

  getWeekRange(week: CalendarWeek<any>) {
    const startMonth = this.formatMonth(week.months[0]);
    const endMonth = this.formatMonth(week.months[1]);

    return [
      `${startMonth} ${week.years[0]}`,
      `${endMonth ? endMonth : startMonth} ${week.years.length === 1 ? week.years[0] : week.years[1]}`,
    ];
  }

  selectBooking() {
    this.selectedBooking.emit({ nativeElement: this.bookingRef.nativeElement, options: this.day });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
